import React, { PureComponent } from 'react'
import T from 'prop-types'
import _ from 'lodash'
import IT from 'react-immutable-proptypes'
import {
  // TextInput,
  ScrollBars,
  // CircleLoader,
  AnimateHeight,
  SaveButton,
  ErrorState,
  DataTable,
  // Button
  DimensionAware,
  TextOverflowCenterLite
} from 'react-base'
import {
  is,
  Map,
  List,
  OrderedMap,
  Iterable,
  Seq,
  Set
} from 'immutable'
// import TableHeader from './TableHeader'
import Cell from './Cell'
import ColHeaderCell from './ColHeaderCell'
import RowHeaderCell from './RowHeaderCell'
import rafSchedule from 'raf-schd'
import {
  SCHEMA_SHAPE
} from '../shapes'
// import MultiGrid from './MultiGridCustom2'
// import {MultiGrid} from 'react-virtualized'
import memoize from 'memoize-one'

const MIN_CELL_HEIGHT = 40

// const DEFAULT_COL_WIDTH = 100
// const DEFAULT_COL = Map({
//   width: DEFAULT_COL_WIDTH
// })


// const LoadingCellPlaceholder = rowIndex => (
//   <div className={`scroll_placeholder size_${ rowIndex % 3 ? (rowIndex % 5 ? "third" : "odd") : "even" }`} />
// )

const getVisibleRows = memoize((scrollTop, height, rowDims, rowBufferPx) => Seq(rowDims).filter(
  ([rowKey, rowIndex, rowHeight, rowOffsetTop, isStickyRow]) => isStickyRow || (rowOffsetTop + rowHeight >= scrollTop - rowBufferPx && rowOffsetTop < scrollTop + height + rowBufferPx)
))


const getVisibleCols = memoize((scrollLeft, width, colDims, colBufferPx) => Seq(colDims).filter(
  ([colKey, colIndex, colWidth, colOffsetLeft, isStickyCol]) => isStickyCol || (colOffsetLeft + colWidth >= scrollLeft - colBufferPx && colOffsetLeft < scrollLeft + width + colBufferPx)
))

class Grid extends PureComponent {
  static displayName = 'Schemer.Grid'

  static propTypes = {
    rows: IT.orderedMap,
    columns: IT.orderedMap,
    schema: SCHEMA_SHAPE.isRequired,
    // getRowKey: T.func, // Function that returns a unique key for a given row object - (rowObject) => <row key>. Defaults to looking for an `id` property

    height: T.number,
    width: T.number,

    rowBufferPx: T.number,
    colBufferPx: T.number,

    stickyRowKeys: IT.setOf(T.oneOfType([T.number, T.string])),
    stickyColKeys: IT.setOf(T.number),
    onStickyColChange: T.func.isRequired,
    onStickyRowChange: T.func.isRequired,

    columnHeaderComponent: T.oneOfType([T.element, T.func]), // React Component or Functional/Stateless component
    columnHeaderComponentProps: IT.mapOf(T.any, T.string),
    rowHeaderComponent: T.oneOfType([T.element, T.func]), // React Component or Functional/Stateless component
    rowHeaderComponentProps: IT.mapOf(T.any, T.string),

    renderRowHeader: T.bool, // If true, will render a row "header". left-aligned, permanently sticky.
    rowHeaderWidth: T.number,

    readOnly: T.bool,
    onChange: T.func
  }

  static defaultProps = {
    rows: OrderedMap(),
    columns: List(),
    height: 100,
    width: 100,
    // getRowKey: defaultRowKeyFn,
    rowBufferPx: 100,
    colBufferPx: 100,
    rowHeaderWidth: 40
  }

  constructor(props) {
    super(props)

    this.state = {
      scrollLeft: 0,
      scrollTop: 0,

      rowDims: Seq(),
      colDims: Seq(),

      headerHeight: 0,
      totalHeight: 0,
      totalWidth: 0,

      visibleRows: Seq(),
      visibleCols: Seq(),

      focusedRowKeys: Set()
    }
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    const {
      schema,
      rows,
      columns,
      stickyRowKeys,
      stickyColKeys,
      height,
      width,
      rowBufferPx,
      colBufferPx,
      renderRowHeader,
      rowHeaderWidth

    } = nextProps
    const {
      scrollTop,
      scrollLeft
    } = prevState

    // Pre-compute semi-dynamic row heights and cell offsets
    let colOffsetLeft = renderRowHeader ? rowHeaderWidth : 0
    let stickyColOffsetLeft = renderRowHeader ? rowHeaderWidth : 0 // Start offset from the row header
    let headerHeight = MIN_CELL_HEIGHT

    const colKeys = columns.keySeq().toArray()

    const newColDims = columns.map((column, columnKey) => {
      const colIndex = colKeys.indexOf(columnKey)
      const colWidth = column.get('width')
      const isStickyCol = stickyColKeys && stickyColKeys.includes(columnKey)

      const _out = List([
        columnKey,
        colIndex,
        colWidth,
        colOffsetLeft,
        isStickyCol,
        stickyColOffsetLeft
      ])
      colOffsetLeft += colWidth
      if (isStickyCol) {
        stickyColOffsetLeft += colWidth
      }

      const columnHeightDef = column.get('headerHeight', MIN_CELL_HEIGHT)
      const headerCellHeight = typeof columnHeightDef === 'function' ? columnHeightDef(schema.get(columnKey)) : columnHeightDef
      headerHeight = headerCellHeight > headerHeight ? headerCellHeight : headerHeight

      return _out
    })

    let rowOffsetTop = headerHeight
    let stickyRowOffsetTop = headerHeight

    const rowKeys = rows.keySeq().toArray()

    const newRowDims = rows.map((row, rowKey) => {
      const rowIndex = rowKeys.indexOf(rowKey)

      const rowHeight = columns.reduce((maxHeight, column, columnKey) => {
        const columnHeightDef = column.get('height', MIN_CELL_HEIGHT)
        let cellHeight = typeof columnHeightDef === 'function' ? columnHeightDef(row.get(columnKey)) : columnHeightDef
        cellHeight = cellHeight > MIN_CELL_HEIGHT ? cellHeight : MIN_CELL_HEIGHT
        return cellHeight > maxHeight ? cellHeight : maxHeight
      }, MIN_CELL_HEIGHT)

      const isStickyRow = stickyRowKeys && stickyRowKeys.includes(rowKey)
      const _out = List([
        rowKey,
        rowIndex,
        rowHeight,
        rowOffsetTop,
        isStickyRow,
        stickyRowOffsetTop
      ])

      rowOffsetTop += rowHeight
      if (isStickyRow) {
        stickyRowOffsetTop += rowHeight
      }

      return _out
    })

    // Store dimensions as fast-iterable sequences
    const rowDims = newRowDims.valueSeq()
    const colDims = newColDims.valueSeq()

    return {
      rowDims,
      colDims,
      totalHeight: rowOffsetTop,
      totalWidth: colOffsetLeft,
      totalStickyHeight: stickyRowOffsetTop,
      totalStickyWidth: stickyColOffsetLeft,
      headerHeight: headerHeight,
      visibleRows: getVisibleRows(scrollTop, height, rowDims, rowBufferPx),
      visibleCols: getVisibleCols(scrollLeft, width, colDims, colBufferPx)
    }
  }

  componentWillUnmount() {
    this.handleScrollThrottled.cancel()
  }

  // _onScroll = scrollNode => {
  //   const {
  //     scrollTop,
  //     scrollLeft
  //   } = scrollNode
  //   // const {
  //   //   height,
  //   //   width,
  //   //   rowBufferPx,
  //   //   colBufferPx
  //   // } = this.props
  //   // const {
  //   //   colDims,
  //   //   rowDims,
  //   //   visibleRows,
  //   //   visibleCols
  //   // } = this.state

  //   // // console.time('_getVisibleOnScroll')
  //   // const newVisibleRows = getVisibleRows(scrollTop, height, rowDims, rowBufferPx)
  //   // const newVisibleCols = getVisibleCols(scrollLeft, width, colDims, colBufferPx)
  //   // // console.timeEnd('_getVisibleOnScroll')

  //   const newState = {
  //     scrollLeft,
  //     scrollTop
  //   }
  //   const needsStateUpdate = true
  //   // if (!is(visibleRows, newVisibleRows)) {
  //   //   newState.visibleRows = newVisibleRows
  //   // }
  //   // if (!is(visibleCols, newVisibleCols)) {
  //   //   newState.visibleCols = newVisibleCols
  //   // }
  //   if (needsStateUpdate) {
  //      this.setState(newState)
  //   }
  // }
  // handleScroll = scrollNode => {
  //   this.setState({
  //     scrollLeft: scrollNode.scrollLeft,
  //     scrollTop: scrollNode.scrollTop
  //   })
  // }

  _onScrollSeparate = scrollNode => {
    const {
      scrollTop,
      scrollLeft
    } = scrollNode

    this.setState({
      scrollLeft,
      scrollTop
    })
    this._checkVisibleCellsThrottled(scrollLeft, scrollTop)
  }

  _checkVisibleCells = (scrollLeft, scrollTop) => {
    const {
      height,
      width,
      rowBufferPx,
      colBufferPx
    } = this.props
    const {
      colDims,
      rowDims,
      visibleRows,
      visibleCols,
      // scrollTop,
      // scrollLeft
    } = this.state

    // console.time('_getVisibleOnScroll')
    const newVisibleRows = getVisibleRows(scrollTop, height, rowDims, rowBufferPx)
    const newVisibleCols = getVisibleCols(scrollLeft, width, colDims, colBufferPx)
    // console.timeEnd('_getVisibleOnScroll')

    const newState = {}
    let needsStateUpdate = false
    if (!is(visibleRows, newVisibleRows)) {
      needsStateUpdate = true
      newState.visibleRows = newVisibleRows
    }
    if (!is(visibleCols, newVisibleCols)) {
      needsStateUpdate = true
      newState.visibleCols = newVisibleCols
    }
    if (needsStateUpdate) {
      this.setState(newState)
    }
  }

  _checkVisibleCellsThrottled = _.throttle(this._checkVisibleCells, 90)
  // handleScrollThrottled = _.throttle(this._onScrollSeparate, 96)
  handleScrollThrottled = rafSchedule(this._onScrollSeparate)
  // handleScrollThrottled = rafSchedule(this.handleScroll)

  _handleScrollBarsRef = inst => {
    this._scrollBarsRef = inst
  }

  // handleCellFocus = (rowIndex, columnKey) => {
  //   console.log('cell focus', rowIndex, columnKey)

  //   //TODO highlight whole row
  //   //TODO make row sticky when focused?
  //   this.setState({
  //     focusedRowKeys: this.state.focusedRowKeys.add(rowIndex)
  //   })
  // }

  // handleCellBlur = (rowIndex, columnKey) => {
  //   console.log('cell blur', rowIndex, columnKey)
  //   this.setState({
  //     focusedRowKeys: this.state.focusedRowKeys.delete(rowIndex)
  //   })
  // }

  // handleCellChange = (rowIndex, columnKey, val) => {
  //   console.log('cell CHANGE', rowIndex, columnKey, val)
  //   const {
  //     onChange
  //   } = this.props

  //   onChange()
  // }

  render() {
    const  {
      columns,
      rows,

      height,
      width,

      schema,
      onStickyColChange,
      onStickyRowChange,

      columnHeaderComponent,
      columnHeaderComponentProps,

      rowHeaderComponent,
      rowHeaderComponentProps,

      renderRowHeader,
      rowHeaderWidth,

      readOnly,
      onChange
    } = this.props
    const {
      scrollTop,
      scrollLeft,
      totalHeight,
      totalWidth,
      totalStickyHeight,
      totalStickyWidth,
      visibleRows,
      visibleCols,
      headerHeight,
      focusedRowKeys
    } = this.state

    const rowCount = rows && rows.size ? rows.size : 0
    const colCount = columns && columns.size ? columns.size : 0

    return (
      <div className="schemer_table_grid">
        <ScrollBars
          onScroll={this.handleScrollThrottled}
          slimShady
          slimShadyDarker
          className={`table_grid_scrollbars`}
          getRef={this._handleScrollBarsRef}
        >
          <div
            className={`rows_wrap`}
            style={{
              height: totalHeight,
              width: totalWidth
            }}
          >
            <div
              className={`schemer_header_row ${scrollTop > 0 ? 'stuck' : ''}`}
              style={{
                height: headerHeight
              }}
            >
              {renderRowHeader &&
                <div
                  className={`schemer_header_cell schemer_row_header_cell schemer_cell sticky stuck stuck_left`}
                  style={{
                    width: rowHeaderWidth
                  }}
                >
                  <RowHeaderCell
                    isOrigin
                    rowHeaderComponent={rowHeaderComponent}
                    rowHeaderComponentProps={rowHeaderComponentProps}
                    canBeStickied={false}
                    schema={schema}
                    row={null}
                    rowIndex={-1}
                    rowKey={"origin"}
                  />
                </div>
              }
              {visibleCols.map(([
                colKey,
                colIndex,
                colWidth,
                colOffsetLeft,
                isStickyCol,
                stickyColOffsetLeft
              ]) => {
                const column = columns.get(colKey)
                const isStickiedLeft = isStickyCol && scrollLeft + stickyColOffsetLeft >= colOffsetLeft
                const isStickiedRight = isStickyCol && !isStickiedLeft && scrollLeft + width - totalStickyWidth + stickyColOffsetLeft <= colOffsetLeft

                return (
                  <div
                    key={colKey}
                    className={`schemer_header_cell schemer_cell ${isStickyCol ? 'sticky' : ''} ${isStickiedLeft || isStickiedRight ? 'stuck': ''} ${isStickiedLeft ? 'stuck_left' : ''} ${isStickiedRight ? 'stuck_right' : ''}`}
                    style={{
                      width: colWidth,
                      left: isStickiedLeft ? stickyColOffsetLeft : isStickiedRight ? (width - totalStickyWidth + stickyColOffsetLeft) : colOffsetLeft,
                      WebkitPosition: isStickiedLeft || isStickiedRight ? 'sticky' : 'absolute',
                      position: isStickiedLeft || isStickiedRight ? 'sticky' : 'absolute',
                      zIndex: isStickiedLeft ? colIndex + 1 : isStickiedRight ? colCount - (colIndex + 1) : 0
                    }}
                  >
                    <ColHeaderCell
                      column={column}
                      columnKey={colKey}
                      isSticky={isStickyCol}
                      onStickyChange={onStickyColChange}
                      schema={schema}
                      columnHeaderComponent={columnHeaderComponent}
                      columnHeaderComponentProps={columnHeaderComponentProps}
                    />
                  </div>
                )
              })}
            </div>
            {visibleRows.map(([
              rowKey,
              rowIndex,
              rowHeight,
              rowOffsetTop,
              isStickyRow,
              stickyRowOffsetTop
            ]) => {
              const isOddRow = rowIndex % 2
              const isStickiedTop = isStickyRow && scrollTop + stickyRowOffsetTop >= rowOffsetTop
              const isStickiedBottom = isStickyRow && !isStickiedTop && scrollTop + height - totalStickyHeight + stickyRowOffsetTop <= rowOffsetTop
              const isRowFocused = focusedRowKeys.has(rowKey)
              const row = rows.get(rowKey)

              return (
                <div
                  className={`schemer_row ${isStickyRow ? 'sticky' : ''} ${isStickiedTop ? 'stuck stuck_top' : ''} ${isStickiedBottom ? 'stuck stuck_bottom' : ''} ${isRowFocused ? 'focused_row' : ''}`}
                  key={rowKey}
                  style={{
                    height: rowHeight,
                    top: isStickiedTop ? stickyRowOffsetTop : isStickiedBottom ? (height - totalStickyHeight + stickyRowOffsetTop) : rowOffsetTop,
                    WebkitPosition: isStickiedTop || isStickiedBottom ? 'sticky' : 'absolute',
                    position: isStickiedTop || isStickiedBottom ? 'sticky' : 'absolute',
                    zIndex: isStickiedTop ? rowIndex + 1 : isStickiedBottom ? rowCount - (rowIndex + 1) : 0
                  }}
                >
                  {renderRowHeader &&
                    <div
                      className={`schemer_row_header_cell schemer_cell sticky stuck stuck_left ${isOddRow ? 'odd' : 'even'}`}
                      style={{
                        width: rowHeaderWidth
                      }}
                    >
                      <RowHeaderCell
                        rowIndex={rowIndex}
                        rowKey={rowKey}
                        row={row}
                        schema={schema}
                        isSticky={isStickyRow}
                        onStickyChange={onStickyRowChange}
                        rowHeaderComponent={rowHeaderComponent}
                        rowHeaderComponentProps={rowHeaderComponentProps}
                      />
                    </div>
                  }
                  {visibleCols.map(([
                    colKey,
                    colIndex,
                    colWidth,
                    colOffsetLeft,
                    isStickyCol,
                    stickyColOffsetLeft
                  ]) => {
                    const column = columns.get(colKey)
                    const isStickiedLeft = isStickyCol && scrollLeft + stickyColOffsetLeft >= colOffsetLeft
                    const isStickiedRight = isStickyCol && !isStickiedLeft && scrollLeft + width - totalStickyWidth + stickyColOffsetLeft <= colOffsetLeft

                    return (
                      <div
                        key={colKey}
                        className={`schemer_cell ${isOddRow ? 'odd' : 'even'} ${isStickyCol || isStickyRow ? 'sticky' : ''} ${isStickiedLeft || isStickiedTop || isStickiedBottom || isStickiedRight ? 'stuck': ''} ${isStickiedLeft ? 'stuck_left' : ''} ${isStickiedRight ? 'stuck_right' : ''}`}
                        style={{
                          width: colWidth,
                          left: isStickiedLeft ? stickyColOffsetLeft : isStickiedRight ? (width - totalStickyWidth + stickyColOffsetLeft) : colOffsetLeft,
                          WebkitPosition: isStickiedLeft || isStickiedRight ? 'sticky' : 'absolute',
                          position: isStickiedLeft || isStickiedRight ? 'sticky' : 'absolute',
                          zIndex: isStickiedLeft ? colIndex + 1 : isStickiedRight ? colCount - (colIndex + 1) : 0
                        }}
                      >
                        <Cell
                          column={column}
                          row={row}
                          columnKey={colKey}
                          rowIndex={rowIndex}
                          rowKey={rowKey}
                          schema={schema}
                          onFocus={this.handleCellFocus}
                          onBlur={this.handleCellBlur}
                          onChange={onChange}
                          readOnly={readOnly}
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </ScrollBars>
      </div>
    )
  }
}

export default DimensionAware(Grid)